import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AsyncListShell } from "@/components/admin/AsyncListShell";

const ESTADOS = ["Sin contrato", "Contrato firmado", "Vencido", "Renovado"];
const ESTADO_COLORS = {
  "Sin contrato": "bg-[#F1F3F3] text-[#3E5641]",
  "Contrato firmado": "bg-[#A0553F] text-white",
  "Vencido": "bg-red-50 text-red-700",
  "Renovado": "bg-[#C4674F]/15 text-[#A0553F]",
};

const EMPTY = { nombre: "", telefono: "", correo: "", vehiculos_asociados: [], estado_contrato: "Sin contrato", garantia_retenida: "", notas: "" };

export default function ConsignorsPage() {
  const [items, setItems] = useState([]);
  const [inv, setInv] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([api.get("/consignors"), api.get("/inventory")]);
      setItems(a.data); setInv(b.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => items.filter((it) => {
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return [it.nombre, it.telefono, it.correo].filter(Boolean).some((v) => v.toLowerCase().includes(t));
  }), [items, q]);

  async function onDelete(id) {
    if (!window.confirm("¿Eliminar consignante?")) return;
    try { await api.delete(`/consignors/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div data-testid="admin-consignors">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mono text-xs uppercase tracking-widest text-[#A0553F]">Módulo 02</p>
          <h1 className="mt-1 font-serif text-3xl text-[#3E5641] md:text-4xl">Consignantes</h1>
          <p className="mt-1 text-sm text-[#6B7678]">Dueños de autos y sus contratos de consignación.</p>
        </div>
        <button className="btn-primary" onClick={() => setEditing({ ...EMPTY })} data-testid="cons-new-btn"><Plus size={16} /> Nuevo consignante</button>
      </header>

      <Input placeholder="Buscar por nombre, teléfono o correo..." value={q} onChange={(e) => setQ(e.target.value)} className="mb-4 max-w-sm" data-testid="cons-search" />

      <AsyncListShell
        loading={loading}
        isEmpty={filtered.length === 0}
        loadingTestId="cons-loading"
        emptyTestId="cons-empty"
        emptyLabel="Sin consignantes aún."
      >
        <div className="overflow-hidden rounded-sm border border-[#3E5641]/10 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F1F3F3] text-left text-xs uppercase tracking-wider text-[#6B7678]">
                <tr>
                  <th className="px-4 py-3">Nombre</th>
                  <th className="px-4 py-3">Contacto</th>
                  <th className="px-4 py-3">Vehículos</th>
                  <th className="px-4 py-3">Contrato</th>
                  <th className="px-4 py-3">Garantía retenida</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#3E5641]/10">
                {filtered.map((c) => (
                  <tr key={c.id} className="hover:bg-[#F1F3F3]/50" data-testid={`cons-row-${c.id}`}>
                    <td className="px-4 py-3 font-medium text-[#3E5641]">{c.nombre}</td>
                    <td className="px-4 py-3">
                      <div>{c.telefono}</div>
                      <div className="text-xs text-[#6B7678]">{c.correo || "—"}</div>
                    </td>
                    <td className="px-4 py-3 mono text-xs">
                      {c.vehiculos_asociados?.length
                        ? c.vehiculos_asociados.map((id) => inv.find((v) => v.id === id)?.folio || id.slice(0, 6)).join(", ")
                        : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block rounded-sm px-2 py-1 text-xs ${ESTADO_COLORS[c.estado_contrato]}`}>{c.estado_contrato}</span>
                    </td>
                    <td className="px-4 py-3 mono">{c.garantia_retenida ? `$${Number(c.garantia_retenida).toLocaleString()}` : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setEditing(c)} className="mr-2 p-1 hover:bg-[#F1F3F3]" data-testid={`cons-edit-${c.id}`}><Pencil size={14} /></button>
                      <button onClick={() => onDelete(c.id)} className="p-1 text-red-600 hover:bg-red-50" data-testid={`cons-delete-${c.id}`}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </AsyncListShell>

      {editing && <ConsignorDialog item={editing} inv={inv} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ConsignorDialog({ item, inv, onClose, onSaved }) {
  const isNew = !item.id;
  const [form, setForm] = useState({
    ...EMPTY,
    ...item,
    garantia_retenida: item.garantia_retenida ?? "",
    correo: item.correo ?? "",
    notas: item.notas ?? "",
  });
  const [saving, setSaving] = useState(false);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  function toggleVeh(id) {
    setForm((f) => ({
      ...f,
      vehiculos_asociados: f.vehiculos_asociados.includes(id)
        ? f.vehiculos_asociados.filter((x) => x !== id)
        : [...f.vehiculos_asociados, id],
    }));
  }

  async function save() {
    setSaving(true);
    try {
      const payload = {
        nombre: form.nombre.trim(),
        telefono: form.telefono.trim(),
        correo: form.correo?.trim() || null,
        vehiculos_asociados: form.vehiculos_asociados,
        estado_contrato: form.estado_contrato,
        garantia_retenida: form.garantia_retenida === "" ? null : Number(form.garantia_retenida),
        notas: form.notas?.trim() || null,
      };
      if (isNew) await api.post("/consignors", payload);
      else await api.patch(`/consignors/${item.id}`, payload);
      toast.success(isNew ? "Consignante creado" : "Consignante actualizado");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{isNew ? "Nuevo consignante" : form.nombre}</DialogTitle></DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <F label="Nombre completo *"><Input required value={form.nombre} onChange={(e) => setF("nombre", e.target.value)} data-testid="cons-nombre" /></F>
          <F label="Teléfono *"><Input required value={form.telefono} onChange={(e) => setF("telefono", e.target.value)} data-testid="cons-tel" /></F>
          <F label="Correo"><Input type="email" value={form.correo} onChange={(e) => setF("correo", e.target.value)} data-testid="cons-correo" /></F>
          <div>
            <Label className="field-label">Estado del contrato</Label>
            <Select value={form.estado_contrato} onValueChange={(v) => setF("estado_contrato", v)}>
              <SelectTrigger data-testid="cons-estado"><SelectValue /></SelectTrigger>
              <SelectContent>{ESTADOS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <F label="Garantía retenida ($)"><Input type="number" value={form.garantia_retenida} onChange={(e) => setF("garantia_retenida", e.target.value)} data-testid="cons-garantia" /></F>
        </div>
        <div>
          <Label className="field-label">Vehículos asociados (inventario)</Label>
          <div className="max-h-40 overflow-y-auto rounded-sm border border-[#3E5641]/10 p-3 text-sm">
            {inv.length === 0 && <p className="text-[#6B7678]">Sin autos en inventario.</p>}
            {inv.map((v) => (
              <label key={v.id} className="flex cursor-pointer items-center gap-2 py-1">
                <input type="checkbox" checked={form.vehiculos_asociados.includes(v.id)} onChange={() => toggleVeh(v.id)} />
                <span className="mono text-xs">{v.folio}</span>
                <span className="text-[#3E5641]">{v.marca} {v.modelo}</span>
                <span className="text-xs text-[#6B7678]">{v.anio}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <Label className="field-label">Notas</Label>
          <Textarea rows={3} value={form.notas} onChange={(e) => setF("notas", e.target.value)} data-testid="cons-notas" />
        </div>
        <DialogFooter>
          <button onClick={onClose} className="btn-outline-dark">Cancelar</button>
          <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-60" data-testid="cons-save">
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children }) {
  return <div><Label className="field-label">{label}</Label>{children}</div>;
}
